import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  GoogleSigninButtonModule,
  SocialAuthService,
  SocialUser,
} from '@abacritt/angularx-social-login';
import { Subscription } from 'rxjs';
import { AuthService, LoginResult } from '../../../shared/services/auth.service';
import { LogoComponent } from '../../../shared/components/logo/logo.component';
import { SessionStore } from '../../../shared/stores/session.store';

@Component({
  selector: 'app-login-page',
  imports: [MatProgressSpinnerModule, GoogleSigninButtonModule, LogoComponent],
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage implements OnInit, AfterViewInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly sessionStore = inject(SessionStore);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  readonly wrapper = viewChild<ElementRef<HTMLElement>>('wrapper');

  readonly hasError = signal('');
  readonly isSubmitting = signal(false);
  readonly buttonWidth = signal(400);
  readonly buttonSize = signal<'small' | 'medium' | 'large'>('large');

  private authStateSub: Subscription | null = null;
  private viewportObserver: ResizeObserver | null = null;

  ngOnInit(): void {
    const session = this.sessionStore.currentSession();
    if (session) {
      this.router.navigate(['/']);
    }

    this.authStateSub = this.socialAuthService.authState.subscribe((user: SocialUser) => {
      if (!user?.idToken || this.isSubmitting()) return;
      this.handleGoogleLogin(user.idToken);
    });
  }

  ngAfterViewInit(): void {
    const el = this.wrapper()?.nativeElement;
    if (!el) return;

    this.viewportObserver = new ResizeObserver((entries) => {
      this.zone.run(() => {
        const width = entries[0]?.contentRect.width ?? 300;
        const clamped = Math.min(Math.max(width, 200), 400);
        this.buttonWidth.set(clamped);
        this.buttonSize.set(clamped >= 375 ? 'large' : 'small');
      });
    });
    this.viewportObserver.observe(el);
  }

  ngOnDestroy(): void {
    this.authStateSub?.unsubscribe();
    this.viewportObserver?.disconnect();
  }

  private handleGoogleLogin(idToken: string): void {
    this.isSubmitting.set(true);
    this.hasError.set('');

    this.authService.login(idToken).subscribe({
      next: (res: LoginResult) => {
        if (res.success) {
          this.router.navigate(['/']);
          this.isSubmitting.set(false);
        } else {
          const message = res.fieldErrors?.length
            ? res.fieldErrors.map((e) => e.message).join(' ')
            : res.message;
          this.hasError.set(message);
          this.isSubmitting.set(false);
          this.socialAuthService.signOut(true).catch(() => undefined);
        }
      },
      error: () => {
        this.hasError.set('Login failed. Please try again.');
        this.isSubmitting.set(false);
        this.socialAuthService.signOut(true).catch(() => undefined);
      },
    });
  }
}
